import React, { lazy, startTransition, Suspense, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material'
import { Edit, ExpandMore } from '@mui/icons-material'
import { GRAY_COLORS, ERROR_COLORS, INFO_COLORS } from 'asterX-UI'
import FieldInfoNG from 'asterX-UI/DataDisplay/FieldInfoNG'
import { NotFound } from 'components/NotFound/NotFound'
import { AsterChip } from 'asterX-UI/Chip'
import { getFullName, getPledgeName, getTypeName } from 'lib/utils'
import { useAuthStore } from 'context/AuthStore'
import { MANAGER } from 'lib/constants/roles'
import { useContractStore } from 'context/ContractStore'

const UpdateCollateralModal = lazy(() => import('components/RoleModals/UpdateCollateralModal/UpdateCollateralModal'))
const UserRevertModal = lazy(() => import('components/RoleModals/UserRevertModal/UserRevertModal'))

const styles = {
  accordion: expanded => ({
    border: `1px solid ${GRAY_COLORS[200]}`,
    padding: expanded ? '20px' : '0 20px 0 20px'
  }),
  chipBox: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    backgroundColor: GRAY_COLORS[100],
    border: `1px solid ${GRAY_COLORS[200]}`,
    padding: '2px 8px',
    borderRadius: '16px'
  },
  childChipBox: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: `1px solid ${GRAY_COLORS[50]}`,
    padding: '0 8px',
    borderRadius: '16px',
    maxWidth: '100%',
    overflowX: 'auto',
    whiteSpace: 'nowrap'
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    marginRight: 'auto'
  },
  description: {
    fontSize: 14,
    fontWeight: 600,
    wordBreak: 'break-word',
    color: GRAY_COLORS[900]
  },
  chipTitle: {
    fontSize: 12,
    fontWeight: 500
  },
  chipText: {
    color: INFO_COLORS[700],
    fontSize: 12
  }
}
export const PledgeAccordion = ({ title = 'Залог', data, sections, columns = '3', index = 0,
                                  isEdit = null,readonly = false }) => {
  const { authStore } = useAuthStore()
  const {
    contractStore: { loanApplicationData },
    contractStoreFunc: { setManagerWorking }
  } = useContractStore()
  const [expanded, setExpanded] = useState(true)
  const [openEdit, setOpenEdit] = useState(false)

  const openEditModal = (e, isEditType) => {
    if (readonly) return
    e.stopPropagation()
    setManagerWorking(true)
    startTransition(() => {
      setOpenEdit(isEditType)
    })
  }

  const closeEditModal = () => {
    setManagerWorking(false)
    setOpenEdit(null)
  }

  const handleChange = () => {
    setExpanded(prev => !prev)
  }

  return (
    <>
      <Suspense
        fallback={
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        }
      >
        <UpdateCollateralModal
          data={data}
          openModal={openEdit === 'additionalInformation'}
          closeModal={closeEditModal}
        />
        {['guarantor-data', 'collateral'].includes(openEdit) && (
          <UserRevertModal data={data} openModal={openEdit} closeModal={closeEditModal} />
        )}
      </Suspense>
      <Accordion expanded={expanded} onChange={handleChange} sx={styles.accordion(expanded)}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel-content" id="panel-header">
          <Typography variant="subtitle1">
            {title} {index + 1}
          </Typography>
          {isEdit && loanApplicationData?.status?.code === 'userRevert' && !readonly && (
            <IconButton
              sx={{
                padding: 1,
                margin: 0,
                marginLeft: 'auto',
                '&:hover': {
                  backgroundColor: INFO_COLORS[400]
                },
                backgroundColor: GRAY_COLORS[200]
              }}
              onClick={e => openEditModal(e, isEdit)}
            >
              <Edit />
            </IconButton>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ paddingBottom: '0' }}>
          {sections && sections.length > 0 ? (
            <Box display="grid" gridTemplateColumns={`repeat(${columns}, 1fr)`} gap={4}>
              {sections.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  {section.hasChip && (
                    <Box
                      gridColumn={`span ${columns}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}
                    >
                      <AsterChip
                        label={getTypeName(data?.type)}
                        size="small"
                        style={{
                          border: `1px solid ${ERROR_COLORS[400]}`,
                          backgroundColor: ERROR_COLORS[50],
                          color: ERROR_COLORS[700]
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '10px'
                        }}
                      >
                        <Box sx={styles.chipBox}>
                          <Typography sx={styles.chipTitle}>Залог</Typography>
                          <Box sx={styles.childChipBox}>
                            <Typography sx={styles.chipText}>{`${getPledgeName(data)} ->`}</Typography>
                          </Box>
                        </Box>
                        <Box sx={styles.chipBox}>
                          <Typography sx={styles.chipTitle}>Залогодатель</Typography>
                          <Box sx={styles.childChipBox}>
                            <Typography sx={styles.chipText}>{`${getFullName(
                              data?.clientGuarantee?.firstName,
                              data?.clientGuarantee?.lastName,
                              data?.clientGuarantee?.patronymic
                            )} ->`}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {section.title && (
                    <Box gridColumn={`span ${columns}`} sx={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography sx={styles.title}>{section.title}</Typography>
                      {section?.edit &&
                        ['CAR', 'REAL_ESTATE'].includes(data?.type) &&
                        authStore.isUserRole === MANAGER &&
                        loanApplicationData?.status?.code !== 'userRevert' && !readonly && (
                          <IconButton
                            sx={{ padding: 1, margin: 0 }}
                            onClick={e => openEditModal(e, 'additionalInformation')}
                          >
                            <Edit />
                          </IconButton>
                        )}
                    </Box>
                  )}
                  {section.data.map((item, index) => (
                    <FieldInfoNG key={index} label={item.label} isVertical>
                      <Typography sx={styles.description}>{item.value}</Typography>
                    </FieldInfoNG>
                  ))}
                  {section.line && sectionIndex !== sections.length - 1 && (
                    <Box gridColumn={`span ${columns}`}>
                      <Divider />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <NotFound />
          )}
        </AccordionDetails>
      </Accordion>
    </>
  )
}
